import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import PlayArrow from '@mui/icons-material/PlayArrow';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Error from '@mui/icons-material/Error';
import {
  databaseMaintenanceService,
  MaintenanceOperationInfo,
  MaintenanceExecutionResult,
} from '../../services/databaseMaintenanceService';

export const DatabaseMaintenancePanel: React.FC = () => {
  const [operations, setOperations] = useState<MaintenanceOperationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executing, setExecuting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, MaintenanceExecutionResult>>({});
  const [expandedResults, setExpandedResults] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadOperations();
  }, []);

  const loadOperations = async () => {
    try {
      setLoading(true);
      const data = await databaseMaintenanceService.getOperations();
      setOperations(data);
      setError(null);
    } catch {
      setError('Failed to load maintenance operations');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async (operationId: string) => {
    try {
      setExecuting(operationId);
      setError(null);
      const result = await databaseMaintenanceService.executeOperation(operationId);
      setResults(prev => ({ ...prev, [operationId]: result }));
      setExpandedResults(prev => ({ ...prev, [operationId]: true }));
    } catch {
      setError(`Failed to execute operation: ${operationId}`);
    } finally {
      setExecuting(null);
    }
  };

  const toggleExpand = (operationId: string) => {
    setExpandedResults(prev => ({ ...prev, [operationId]: !prev[operationId] }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Run predefined maintenance operations across all active tenant databases.
        All operations are idempotent and safe to re-run.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {operations.length === 0 ? (
        <Alert severity="info">No maintenance operations available.</Alert>
      ) : (
        operations.map(op => (
          <Card key={op.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {op.name}
                    </Typography>
                    <Chip label={op.category} size="small" variant="outlined" />
                    {op.isIdempotent && (
                      <Chip label="Idempotent" size="small" color="success" variant="outlined" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {op.description}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={executing === op.id ? <CircularProgress size={16} /> : <PlayArrow />}
                  onClick={() => handleExecute(op.id)}
                  disabled={executing !== null}
                  sx={{ ml: 2, minWidth: 80 }}
                >
                  {executing === op.id ? 'Running' : 'Run'}
                </Button>
              </Box>

              {/* Execution results */}
              {results[op.id] && (
                <Box mt={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => toggleExpand(op.id)}
                  >
                    {results[op.id].success ? (
                      <CheckCircle color="success" fontSize="small" />
                    ) : (
                      <Error color="error" fontSize="small" />
                    )}
                    <Typography variant="body2" fontWeight={500}>
                      {results[op.id].tenantsSucceeded}/{results[op.id].tenantsProcessed} tenants succeeded
                      {results[op.id].tenantsFailed > 0 &&
                        ` (${results[op.id].tenantsFailed} failed)`}
                    </Typography>
                    <IconButton size="small">
                      {expandedResults[op.id] ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>

                  <Collapse in={expandedResults[op.id]}>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tenant</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Details</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {results[op.id].tenantResults.map(tr => (
                            <TableRow key={tr.tenantId}>
                              <TableCell>
                                <Typography variant="body2">{tr.tenantName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {tr.tenantId}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={tr.status}
                                  size="small"
                                  color={
                                    tr.status === 'Applied'
                                      ? 'success'
                                      : tr.status === 'Already Applied'
                                        ? 'info'
                                        : 'error'
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {tr.error && (
                                  <Typography variant="caption" color="error">
                                    {tr.error}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );
};
